<script setup lang="ts">
import { useBillingUtils } from '@/composables/useBillingUtils';
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { Alert } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed } from 'vue';

const props = defineProps<{ class?: string }>();

const billingStore = useBillingStore();
const { couponDiscountBenefit, discountEnd, formatPrice } = useBillingUtils();
const { t } = useI18n();

/** Synthetic row when Stripe returns only an unexpanded discount id (`di_…`) on the subscription. */
const UNEXPANDED_DISCOUNT = '__dabble_unexpanded_discount';

function collectDiscountEntries(source: Record<string, unknown>): unknown[] {
  const out: unknown[] = [];
  if (source.discount != null) out.push(source.discount);
  const top = source.discounts;
  if (Array.isArray(top)) out.push(...top);
  // Subscriptions expose line items at .items.data[]; invoices at .lines.data[].
  for (const key of ['items', 'lines'] as const) {
    const items = (source[key] as { data?: unknown[] } | undefined)?.data;
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const idisc = (item as Record<string, unknown>).discounts;
      if (Array.isArray(idisc)) out.push(...idisc);
    }
  }
  return out;
}

/** Resolve expanded Stripe.Coupon from a discount row (API 2024+ uses `source.coupon`; legacy uses top-level `coupon`). */
function resolveCouponFromDiscountRow(raw: Record<string, unknown>): Stripe.Coupon | null {
  const legacy = raw.coupon;
  if (legacy && typeof legacy === 'object') return legacy as Stripe.Coupon;
  const source = raw.source;
  if (source && typeof source === 'object') {
    const c = (source as Record<string, unknown>).coupon;
    if (c && typeof c === 'object') return c as Stripe.Coupon;
  }
  return null;
}

function discountRowLooksExpanded(r: Record<string, unknown>): boolean {
  if (r.coupon != null || r.promotion_code != null) return true;
  const src = r.source;
  if (src && typeof src === 'object') {
    const s = src as Record<string, unknown>;
    if (s.coupon != null || s.type === 'coupon') return true;
  }
  return false;
}

/**
 * Pick a discount to render: prefer fully-expanded rows (so % off / duration show).
 * Only use a synthetic placeholder when Stripe only sent unexpanded discount ids.
 * Sources are inspected in order — subscription discounts take precedence over
 * customer-level discounts when both exist.
 */
function pickDiscountFromSources(
  sources: (Record<string, unknown> | null | undefined)[],
): Record<string, unknown> | null {
  const allEntries: unknown[] = [];
  for (const src of sources) {
    if (src) allEntries.push(...collectDiscountEntries(src));
  }
  for (const entry of allEntries) {
    if (typeof entry === 'string') continue;
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    if (discountRowLooksExpanded(r)) return r;
  }
  for (const entry of allEntries) {
    if (typeof entry === 'string') {
      const id = entry.trim();
      if (id) {
        return { [UNEXPANDED_DISCOUNT]: true, id } as Record<string, unknown>;
      }
    }
  }
  return null;
}

const discount = computed((): Record<string, unknown> | null => {
  const sub = billingStore.primarySubscription as Record<string, unknown> | null;
  const customer = billingStore.customer as Record<string, unknown> | null;
  const invoice = billingStore.upcomingInvoice as Record<string, unknown> | null;
  return pickDiscountFromSources([sub, customer, invoice]);
});

interface AccountCreditInfo {
  /** Always positive cents — magnitude of credit being added or applied. */
  amount: number;
  currency: string;
  /** 'pending' = will be added to the customer's balance (e.g. proration credit on next invoice).
   *  'available' = already on the customer's balance, will be applied against future charges. */
  mode: 'pending' | 'available';
}

const accountCredit = computed<AccountCreditInfo | null>(() => {
  const customer = billingStore.customer as Stripe.Customer | null;
  const invoice = billingStore.upcomingInvoice as Stripe.Invoice | null;
  const currency = (customer?.currency || invoice?.currency || 'usd') as string;
  const customerBalance = customer?.balance ?? 0;

  // Negative-total invoice: the upcoming invoice is purely credit being issued
  // (e.g. proration refund from a plan downgrade) — Stripe will add it to
  // customer.balance when the invoice posts.
  if (invoice && typeof invoice.total === 'number' && invoice.total < 0) {
    return { amount: -invoice.total, currency, mode: 'pending' };
  }

  // Standing credit on the customer that hasn't been applied yet.
  if (customerBalance < 0) {
    return { amount: -customerBalance, currency, mode: 'available' };
  }

  // Invoice that draws down existing customer credit (starting_balance was negative).
  if (invoice && typeof invoice.starting_balance === 'number' && invoice.starting_balance < 0) {
    return { amount: -invoice.starting_balance, currency, mode: 'available' };
  }

  return null;
});

const accountCreditSummary = computed(() => {
  const credit = accountCredit.value;
  if (!credit) return '';
  const formatted = formatPrice(credit.amount, credit.currency);
  return credit.mode === 'pending'
    ? t('billing_discount_account_credit_pending', { amount: formatted })
    : t('billing_discount_account_credit', { amount: formatted });
});

function isUnexpandedDiscountPlaceholder(raw: Record<string, unknown> | null): boolean {
  return Boolean(raw && raw[UNEXPANDED_DISCOUNT] === true);
}

const couponObject = computed((): Stripe.Coupon | null => {
  const raw = discount.value;
  if (!raw || isUnexpandedDiscountPlaceholder(raw)) return null;
  return resolveCouponFromDiscountRow(raw);
});

/** Stripe internal ids — not shown as the “coupon name” in the title. */
function looksLikeOpaqueStripeId(s: string): boolean {
  return /^(di_|cou_|cpo_|cgw_|sub_|price_|in_|ii_|dp_|du_)/i.test(s.trim());
}

/**
 * Human-facing label: prefer Stripe coupon **name**, then promotion code, then legacy fields.
 */
const couponDisplayName = computed(() => {
  const raw = discount.value;
  if (!raw || isUnexpandedDiscountPlaceholder(raw)) return '';

  const resolved = resolveCouponFromDiscountRow(raw);
  if (resolved) {
    const named = resolved.name != null && String(resolved.name).trim() ? String(resolved.name).trim() : '';
    if (named) return named;
    if (resolved.id && !looksLikeOpaqueStripeId(resolved.id)) return resolved.id;
  }

  const pc = raw.promotion_code;
  if (pc && typeof pc === 'object') {
    const p = pc as Stripe.PromotionCode;
    if (p.code && String(p.code).trim()) return String(p.code).trim();
  }

  const c = raw.coupon;
  if (typeof c === 'string') {
    const s = c.trim();
    if (s && !looksLikeOpaqueStripeId(s)) return s;
    return '';
  }
  if (c && typeof c === 'object') {
    const co = c as Stripe.Coupon;
    const named = co.name != null && String(co.name).trim() ? String(co.name).trim() : '';
    if (named) return named;
    if (co.id && !looksLikeOpaqueStripeId(co.id)) return co.id;
  }

  return '';
});

/** One sentence: “Name: benefit” when both exist; otherwise the benefit or name alone. */
const discountAlertSummary = computed(() => {
  const c = couponObject.value;
  if (!c) return '';
  const benefit = couponDiscountBenefit(c);
  const name = couponDisplayName.value.trim();
  if (name && benefit) return t('billing_discount_alert_line', { name, benefit });
  if (benefit) return benefit;
  return name;
});

const alertTitle = computed(() => {
  if (couponObject.value) return t('billing_coupon');
  const name = couponDisplayName.value;
  if (name) return `${t('billing_coupon')}: ${name}`;
  return t('billing_coupon');
});

const className = computed(() => props.class || '');

const discountForEnd = computed(() => discount.value as unknown as Stripe.Discount);

const showRepeatingTimeLeft = computed(() => {
  if (!couponObject.value || couponObject.value.duration !== 'repeating') return false;
  return Boolean(discountEnd(discountForEnd.value));
});
</script>

<template>
  <Alert v-if="discount" variant="info" :title="alertTitle" :class="className" :no-icon="true">
    <template v-if="couponObject">
      <p class="discount-alert__summary">{{ discountAlertSummary }}</p>
      <p v-if="showRepeatingTimeLeft" class="discount-alert__time-left">
        {{
          t('billing_discount_time_left', {
            time: discountEnd(discountForEnd),
          })
        }}
      </p>
    </template>
    <template v-else>
      {{ t('billing_coupon_applied_short') }}
    </template>
  </Alert>
  <Alert v-if="accountCredit" variant="info" :class="className" :no-icon="true">
    <strong>{{ t('billing_balance_credit_title') }}:</strong> {{ accountCreditSummary }}
  </Alert>
</template>

<style lang="scss" scoped>
.discount-alert__summary {
  margin: 0;
}

.discount-alert__time-left {
  margin: space('xsmall') 0 0;
}
</style>
